<template>
    <div class="flex-1">
        <template v-if="isLoading">
            <skeleton />
            <skeleton />
            <skeleton />
        </template>
        <not-found v-else-if="plan === null" />
        <app-error v-else-if="plan === undefined" />
        <template v-else>
            <purchase-recurring-program
                v-if="isRecurring"
                v-model:contract="recurringContractData"
                :plan="plan"
                :is-editing="false"
            />
            <purchase-program
                v-else
                v-model:contract="contractData"
                :plan="plan"
                :is-editing="false"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import Skeleton from 'primevue/skeleton'
import { computed, ref } from 'vue'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { type FragmentType, graphql } from '~/resources/graphql'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import PurchaseProgram, {
    PurchaseProgramContractFragment
} from '~/components/PurchaseContract/Program/PurchaseProgram.vue'
import PurchaseRecurringProgram, {
    PurchaseRecurringProgramContractFragment
} from '~/components/PurchaseContract/Program/PurchaseRecurringProgram.vue'
import NotFound from '~/components/NotFound/NotFound.vue'
import AppError from '~/components/NotFound/AppError.vue'

const props = defineProps<{ planId: string }>()
const planId = computed(() => props.planId)
const activeFacility = useActiveFacility()

const query = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const { data, isLoading } = useQuery({
    queryKey: [
        'PurchaseProgramWrapperPlanQuery',
        planId,
        activeFacility,
        selectedLanguage
    ],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query PurchaseProgramWrapperPlanQuery(
                    $planId: ID!
                    $input: SimpleSpokeInput
                    $language: Language!
                ) {
                    plan(id: $planId) {
                        ...PurchaseProgramPlanFragment
                        ...PurchaseRecurringProgramPlanFragment
                        terms {
                            billingType
                        }
                    }
                }
            `),
            {
                planId: planId.value,
                input: {
                    facilityId: activeFacility.value.id
                },
                language: selectedLanguage.value
            }
        )
})

const contractData = ref<FragmentType<typeof PurchaseProgramContractFragment>>()
const recurringContractData =
    ref<FragmentType<typeof PurchaseRecurringProgramContractFragment>>()

const plan = computed(() => {
    if (data.value === undefined) {
        return undefined
    }

    if (data.value === null || data.value.plan === null) {
        return null
    }

    return data.value.plan
})

const isRecurring = computed(() => {
    if (plan.value === undefined || plan.value === null) {
        throw new Error()
    }

    return plan.value.terms.billingType === 'RECURRING'
})
</script>
